import React, { useEffect, useState } from 'react';
import { Avatar, Form, Input, Select, Button, Upload, message, Spin, Modal, Tag, Popconfirm } from 'antd';
import { UserOutlined, EditOutlined, CameraOutlined, CheckCircleOutlined, PlusOutlined, DeleteOutlined, FilePdfOutlined, WarningOutlined } from '@ant-design/icons';
import StagePipeline from '../components/StagePipeline';
import AvailabilityEditor from '../components/AvailabilityEditor';
import dayjs from 'dayjs';
import AppLayout from '../components/AppLayout';
import { useUser } from '../context/UserContext';
import { useSearchParams, useParams } from 'react-router-dom';
import api from '../api/client';
import { levelFromCurrentLevel } from '../utils/levelMap';

const LEVEL_ORDER    = ['PSLE', 'O-Level', 'IP', 'JC', 'IB'];
const EDU_LEVELS     = ['Primary', 'Secondary', 'Junior College', 'IB School', 'Polytechnic', 'University', 'Other'];

export default function Profile() {
  const { user, updateUser } = useUser();
  const { id: routeId } = useParams();
  const [params]   = useSearchParams();
  const viewId     = routeId ? Number(routeId) : (params.get('id') ? Number(params.get('id')) : user?.userId);
  const isOwn      = user ? viewId === user.userId : false;

  const [profile, setProfile]       = useState(null);
  const [subjects, setSubjects]     = useState([]);
  const [education, setEducation]   = useState([]);
  const [subjectTypes, setSubjectTypes] = useState([]);
  const [editing, setEditing]       = useState(false);
  const [loading, setLoading]       = useState(true);
  const [saving, setSaving]         = useState(false);
  const [uploading, setUploading]   = useState(false);
  const [coverUploading, setCoverUploading] = useState(false);

  // Reminder data (own tutor view)
  const [ownTemplates, setOwnTemplates] = useState([]);
  const [ownMaterials, setOwnMaterials] = useState([]);

  // Student viewing tutor
  const [subscribedIds, setSubscribedIds] = useState(new Set());
  const [preview, setPreview]             = useState(null);
  const [previewSubjectId, setPreviewSubjectId] = useState(null);
  const [previewStage, setPreviewStage]   = useState(null);
  const [subModal, setSubModal]           = useState(false);
  const [subLoading, setSubLoading]       = useState(false);
  const [selectedSubject, setSelectedSubject] = useState(null);

  // Local edit state for subjects (pending changes)
  const [pendingSubjects, setPendingSubjects] = useState([]);

  const [form]    = Form.useForm();
  const [subForm] = Form.useForm();

  useEffect(() => {
    async function load() {
      try {
        const userRes = await api.get(`/api/users/${viewId}`);
        setProfile(userRes.data);
        form.setFieldsValue({
          name: userRes.data.name,
          username: userRes.data.username,
          gender: userRes.data.gender,
          description: userRes.data.description,
        });
      } catch { setLoading(false); return; }

      try {
        const [subjRes, eduRes] = await Promise.all([
          api.get(`/api/users/${viewId}/subjects`),
          api.get(`/api/users/${viewId}/education`),
        ]);
        setSubjects(subjRes.data);
        setEducation(eduRes.data);
      } catch {}

      if (isOwn && user?.role === 'tutor') {
        try {
          const [tmplRes, matRes, stRes] = await Promise.all([
            api.get('/api/gate/templates/mine'),
            api.get('/api/materials', { params: { user_id: viewId } }),
            api.get('/api/gate/subject-types'),
          ]);
          setOwnTemplates(tmplRes.data);
          setOwnMaterials(matRes.data);
          setSubjectTypes(stRes.data);
        } catch {}
      }

      if (user && !isOwn && user.role === 'student') {
        try {
          const [subsRes, previewRes] = await Promise.all([
            api.get(`/api/users/${user.userId}/subscriptions`),
            api.get(`/api/tutors/${viewId}/preview`),
          ]);
          setSubscribedIds(new Set(subsRes.data.filter(s => s.tutor_id === viewId && s.tutor_subject_id).map(s => s.tutor_subject_id)));
          setPreview(previewRes.data);
        } catch {}
      }

      setLoading(false);
    }
    load();
  }, [viewId]);

  const startEditing = () => {
    form.setFieldsValue({
      name: profile.name,
      username: profile.username,
      gender: profile.gender,
      description: profile.description,
      current_level: profile.current_level || '',
      education: education.map(e => ({ school: e.school, level: e.level })),
    });
    setPendingSubjects(subjects.map(s => ({ ...s, _deleted: false })));
    setEditing(true);
  };

  const save = async (values) => {
    setSaving(true);
    try {
      // 1. Save profile fields
      await api.patch(`/api/users/${user.userId}`, {
        name: values.name,
        username: values.username,
        gender: values.gender,
        description: values.description,
        current_level: values.current_level || null,
      });
      setProfile(p => ({ ...p, name: values.name, username: values.username, gender: values.gender, description: values.description, current_level: values.current_level }));
      if (values.username) updateUser({ username: values.username });
      if (values.name) updateUser({ name: values.name });

      // 2. Save education
      const eduEntries = (values.education || []).filter(e => e?.school?.trim() && e?.level);
      await api.put(`/api/users/${user.userId}/education`, { entries: eduEntries });
      setEducation(eduEntries);

      // 3. Sync subjects — validate rate is set
      const missingRate = pendingSubjects.filter(s => !s._deleted && (!s.monthly_rate || parseFloat(s.monthly_rate) <= 0));
      if (missingRate.length > 0) {
        message.error(`Please set a monthly rate for: ${missingRate.map(s => s.subject_name || 'new subject').join(', ')}`);
        setSaving(false);
        return;
      }

      const toDelete = pendingSubjects.filter(s => s._deleted && s.subject_id);
      const toAdd    = pendingSubjects.filter(s => s._new && !s._deleted);
      const toUpdate = pendingSubjects.filter(s => !s._new && !s._deleted && s._changed);

      await Promise.all([
        ...toDelete.map(s => api.delete(`/api/subjects/${s.subject_id}`)),
        ...toAdd.map(s => api.post(`/api/users/${user.userId}/subjects`, {
          subject_name: s.subject_name, level: s.level,
          monthly_rate: s.monthly_rate, subject_type_key: s.subject_type_key,
        })),
        ...toUpdate.map(s => api.patch(`/api/subjects/${s.subject_id}`, {
          subject_name: s.subject_name, level: s.level,
          monthly_rate: s.monthly_rate, subject_type_key: s.subject_type_key,
        })),
      ]);

      const refreshed = await api.get(`/api/users/${viewId}/subjects`);
      setSubjects(refreshed.data);

      message.success('Profile updated');
      setEditing(false);
    } catch (e) { message.error(e?.response?.data?.error || e?.message || 'Update failed'); }
    finally { setSaving(false); }
  };

  const uploadAvatar = async (file) => {
    setUploading(true);
    try {
      const urlRes = await api.get('/api/upload-url', { params: { fileName: `avatars/${user.userId}_${file.name}`, fileType: file.type } });
      await fetch(urlRes.data.uploadURL, { method: 'PUT', body: file, headers: { 'Content-Type': file.type } });
      const link = urlRes.data.uploadURL.split('?')[0];
      await api.patch(`/api/users/${user.userId}`, { user_avatar_link: link });
      setProfile(p => ({ ...p, user_avatar_link: link }));
      updateUser({ avatar: link });
      message.success('Avatar updated');
    } catch { message.error('Upload failed'); }
    finally { setUploading(false); }
    return false;
  };

  const uploadCover = async (file) => {
    setCoverUploading(true);
    try {
      const urlRes = await api.get('/api/upload-url', { params: { fileName: `covers/${user.userId}_${file.name}`, fileType: file.type } });
      await fetch(urlRes.data.uploadURL, { method: 'PUT', body: file, headers: { 'Content-Type': file.type } });
      const link = urlRes.data.uploadURL.split('?')[0];
      await api.patch(`/api/users/${user.userId}`, { cover_photo_link: link });
      setProfile(p => ({ ...p, cover_photo_link: link }));
      message.success('Cover photo updated');
    } catch { message.error('Upload failed'); }
    finally { setCoverUploading(false); }
    return false;
  };

  const subscribe = async (values) => {
    if (!values.tutor_subject_id) return;
    setSubLoading(true);
    try {
      const today = new Date(), end = new Date();
      end.setMonth(end.getMonth() + Number(values.months));
      const sub = subjects.find(s => s.subject_id === values.tutor_subject_id);
      await api.post('/api/subscriptions', {
        student_id: user.userId, tutor_subject_id: values.tutor_subject_id,
        start_date: today.toISOString().split('T')[0], end_date: end.toISOString().split('T')[0],
        monthly_rate: sub?.monthly_rate || null,
      });
      setSubscribedIds(prev => new Set([...prev, values.tutor_subject_id]));
      setSubModal(false); subForm.resetFields(); setSelectedSubject(null);
      message.success(`Subscribed to ${sub?.subject_name || 'subject'}`);
    } catch (e) { message.error(e.message || 'Subscription failed'); }
    finally { setSubLoading(false); }
  };

  // Subjects grouped by level for non-owner view
  const groupedSubjects = LEVEL_ORDER.reduce((acc, lvl) => {
    const group = subjects.filter(s => s.level === lvl);
    if (group.length) acc.push({ level: lvl, items: group });
    return acc;
  }, []);
  const otherSubjects = subjects.filter(s => !LEVEL_ORDER.includes(s.level));
  if (otherSubjects.length) groupedSubjects.push({ level: 'Other', items: otherSubjects });

  if (loading) return <AppLayout><div style={{ textAlign: 'center', padding: 80 }}><Spin size="large" /></div></AppLayout>;
  if (!profile) return <AppLayout><p style={{ padding: 40, color: '#888' }}>User not found.</p></AppLayout>;

  return (
    <AppLayout>
      <div>
        {/* ── Profile card ── */}
        <div style={{ border: '1px solid #e8e8e8', borderRadius: 12, overflow: 'hidden', marginBottom: 20, background: '#fff' }}>
          {/* Cover */}
          <div style={{ position: 'relative', height: 160,
            background: profile.cover_photo_link
              ? `url(${profile.cover_photo_link}) center/cover no-repeat`
              : 'linear-gradient(135deg, #0a0a0a 0%, #2d2d2d 50%, #1a1a2e 100%)' }}>
            {isOwn && editing && (
              <Upload beforeUpload={uploadCover} showUploadList={false} accept="image/*">
                <Button size="small" icon={<CameraOutlined />} loading={coverUploading}
                  style={{ position: 'absolute', top: 12, right: 12, background: 'rgba(0,0,0,0.45)', color: '#fff', border: 'none', borderRadius: 6 }}>
                  {!coverUploading && 'Edit cover'}
                </Button>
              </Upload>
            )}
          </div>

          <div style={{ padding: '0 24px 24px', position: 'relative' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', flexWrap: 'wrap', gap: 12 }}>
              {/* Avatar */}
              <div style={{ position: 'relative', display: 'inline-block', marginTop: -40 }}>
                <Avatar size={80} src={profile.user_avatar_link || undefined}
                  icon={!profile.user_avatar_link ? <UserOutlined /> : undefined}
                  style={{ background: '#0a0a0a', border: '4px solid #fff', display: 'block' }}>
                  {!profile.user_avatar_link && profile.name?.[0]}
                </Avatar>
                {isOwn && editing && (
                  <Upload beforeUpload={uploadAvatar} showUploadList={false} accept="image/*">
                    <Button size="small" shape="circle" icon={<CameraOutlined />} loading={uploading}
                      style={{ position: 'absolute', bottom: 2, right: 2, background: '#fff', border: '1px solid #e8e8e8', width: 26, height: 26, minWidth: 26, fontSize: 12 }} />
                  </Upload>
                )}
              </div>

              {/* Actions */}
              <div style={{ flexShrink: 0, display: 'flex', gap: 8 }}>
                {isOwn && !editing && (
                  <Button icon={<EditOutlined />} onClick={startEditing} size="small">Edit profile</Button>
                )}
                {isOwn && editing && (
                  <>
                    <Button onClick={() => { setEditing(false); setPendingSubjects([]); }} size="small">Cancel</Button>
                    <Button type="primary" size="small" loading={saving} style={{ background: '#0a0a0a', border: 'none' }}
                      onClick={() => form.submit()}>Save changes</Button>
                  </>
                )}
                {!isOwn && user?.role === 'student' && profile.role_name === 'tutor' && (() => {
                  const unsubscribed = subjects.filter(s => !subscribedIds.has(s.subject_id));
                  if (unsubscribed.length === 0 && subjects.length > 0)
                    return <Button disabled icon={<CheckCircleOutlined />} style={{ color: '#1a7a4a', borderColor: '#1a7a4a' }}>Subscribed</Button>;
                  if (unsubscribed.length > 0)
                    return <Button type="primary" style={{ background: '#0a0a0a', border: 'none' }}
                      onClick={() => { setSelectedSubject(null); subForm.resetFields(); setSubModal(true); }}>Subscribe</Button>;
                  return null;
                })()}
              </div>
            </div>

            {/* Name + info (view mode) */}
            {!editing && (
              <div style={{ marginTop: 14 }}>
                <div style={{ fontSize: 20, fontWeight: 700, color: '#0a0a0a' }}>{profile.name}</div>
                <div style={{ fontSize: 13, color: '#888' }}>@{profile.username}</div>
                <div style={{ fontSize: 13, color: '#666', marginTop: 4 }}>
                  {profile.role_name === 'tutor' ? 'Tutor' : 'Student'}
                </div>
                {profile.current_level && (
                  <div style={{ fontSize: 13, color: '#555', marginTop: 4 }}>
                    Currently: <strong>{profile.current_level}</strong>
                  </div>
                )}
                {education.length > 0 && (
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 8 }}>
                    {education.map((e, i) => (
                      <Tag key={i} style={{ fontSize: 12 }}>{e.school} · {e.level}</Tag>
                    ))}
                  </div>
                )}
                {profile.description && (
                  <p style={{ fontSize: 13, color: '#444', marginTop: 8, marginBottom: 0 }}>{profile.description}</p>
                )}
                {subjects.length > 0 && (() => {
                  const levels   = [...new Set(subjects.map(s => s.level).filter(Boolean))];
                  const rates    = subjects.map(s => parseFloat(s.monthly_rate)).filter(r => !isNaN(r));
                  const minRate  = rates.length ? Math.min(...rates) : null;
                  const maxRate  = rates.length ? Math.max(...rates) : null;
                  const rateStr  = minRate !== null ? (minRate === maxRate ? `$${minRate}/month` : `$${minRate}–$${maxRate}/month`) : null;
                  return (
                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 10, alignItems: 'center' }}>
                      {levels.map(l => <Tag key={l} style={{ fontSize: 12, padding: '2px 8px' }}>{l}</Tag>)}
                      {rateStr && <span style={{ fontSize: 12, color: '#666' }}>{rateStr}</span>}
                    </div>
                  );
                })()}
              </div>
            )}
          </div>
        </div>

        {/* ── Edit form (own profile) ── */}
        {isOwn && editing && (
          <div style={{ border: '1px solid #e8e8e8', borderRadius: 12, background: '#fff', padding: '24px', marginBottom: 20 }}>
            <Form form={form} layout="vertical" onFinish={save} requiredMark={false}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 16px' }}>
                <Form.Item label="Full name" name="name"><Input /></Form.Item>
                <Form.Item label="Username" name="username"><Input prefix="@" /></Form.Item>
              </div>
              <Form.Item label="Gender" name="gender" style={{ width: '50%' }}>
                <Select>
                  <Select.Option value="Male">Male</Select.Option>
                  <Select.Option value="Female">Female</Select.Option>
                  <Select.Option value="Other">Other</Select.Option>
                </Select>
              </Form.Item>
              <Form.Item label="Bio" name="description">
                <Input.TextArea rows={3} placeholder="Tell students about yourself..." />
              </Form.Item>

              {/* Current level — students only */}
              {profile.role_name === 'student' && (
                <Form.Item label="Current school year" name="current_level"
                  help="e.g. P6, Sec 3, JC1, Poly Y2">
                  <Input placeholder="e.g. P6, Sec 3, JC1" style={{ width: 200 }} />
                </Form.Item>
              )}

              {/* Education */}
              <div style={{ marginBottom: 16 }}>
                <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 8 }}>
                  {profile.role_name === 'student' ? 'School' : 'Education background'}
                </div>
                <Form.List name="education" initialValue={education.map(e => ({ school: e.school, level: e.level }))}>
                  {(fields, { add, remove }) => {
                    const levels = EDU_LEVELS;
                    return (
                      <>
                        {fields.map(({ key, name }) => (
                          <div key={key} style={{ display: 'flex', gap: 8, marginBottom: 8, alignItems: 'flex-start' }}>
                            <Form.Item name={[name, 'school']} style={{ flex: 1, marginBottom: 0 }}
                              rules={[{ required: true, message: 'Enter school' }]}>
                              <Input placeholder={profile.role_name === 'student' ? 'School name' : 'School / University'} />
                            </Form.Item>
                            <Form.Item name={[name, 'level']} style={{ width: 160, marginBottom: 0 }}
                              rules={[{ required: true, message: 'Select level' }]}>
                              <Select placeholder="Level">
                                {levels.map(l => <Select.Option key={l} value={l}>{l}</Select.Option>)}
                              </Select>
                            </Form.Item>
                            <Button type="text" danger icon={<DeleteOutlined />} onClick={() => remove(name)} />
                          </div>
                        ))}
                        <Button type="dashed" icon={<PlusOutlined />} onClick={() => add()} size="small">
                          {profile.role_name === 'student' ? 'Add school' : 'Add education'}
                        </Button>
                      </>
                    );
                  }}
                </Form.List>
              </div>
            </Form>

            {/* Subjects I teach (inline edit) */}
            {profile.role_name === 'tutor' && (
              <div style={{ marginTop: 24 }}>
                <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 12 }}>Subjects I teach</div>
                {pendingSubjects.filter(s => !s._deleted).map((s, i) => (
                  <div key={i} style={{ display: 'flex', gap: 8, marginBottom: 8, alignItems: 'center' }}>
                    <Select style={{ width: 120 }} value={s.level || undefined} placeholder="Level"
                      onChange={val => setPendingSubjects(prev => prev.map((ps, pi) => pi === i
                        ? { ...ps, level: val, subject_name: '', subject_type_key: null, _changed: true }
                        : ps))}>
                      {['PSLE','O-Level','IP','JC','IB'].map(l => <Select.Option key={l} value={l}>{l}</Select.Option>)}
                    </Select>
                    <Select
                      style={{ flex: 1 }}
                      placeholder={s.level ? 'Select subject' : 'Pick a level first'}
                      value={s.subject_name || undefined}
                      showSearch
                      disabled={!s.level}
                      onChange={val => {
                        const st = subjectTypes.find(t => t.display_name === val && t.level === s.level);
                        setPendingSubjects(prev => prev.map((ps, pi) => pi === i
                          ? { ...ps, subject_name: val, subject_type_key: st?.subject_key || null, _changed: true }
                          : ps));
                      }}
                    >
                      {subjectTypes.filter(st => st.level === s.level).map(st => (
                        <Select.Option key={st.subject_key} value={st.display_name}>
                          {st.display_name}
                        </Select.Option>
                      ))}
                    </Select>
                    <Input prefix="$" placeholder="Rate/month *"
                      status={(!s.monthly_rate || parseFloat(s.monthly_rate) <= 0) ? 'error' : ''}
                      style={{ width: 140 }}
                      type="number" min={1} value={s.monthly_rate || ''}
                      onChange={e => setPendingSubjects(prev => prev.map((ps, pi) => pi === i ? { ...ps, monthly_rate: e.target.value, _changed: true } : ps))}
                    />
                    <Popconfirm title="Remove this subject?" onConfirm={() =>
                      setPendingSubjects(prev => prev.map((ps, pi) => pi === i ? { ...ps, _deleted: true } : ps))}>
                      <Button type="text" danger icon={<DeleteOutlined />} />
                    </Popconfirm>
                  </div>
                ))}
                <Button type="dashed" icon={<PlusOutlined />} size="small" onClick={() =>
                  setPendingSubjects(prev => [...prev, { subject_name: '', level: 'O-Level', monthly_rate: '', _new: true, _deleted: false }])}>
                  Add subject
                </Button>
              </div>
            )}
          </div>
        )}

        {/* ── Subjects I teach (tutor own view, not editing) ── */}
        {isOwn && profile.role_name === 'tutor' && !editing && (
          <div style={{ border: '1px solid #e8e8e8', borderRadius: 12, background: '#fff', padding: '20px 24px', marginBottom: 20 }}>
            <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 12 }}>Subjects I teach</div>
            {subjects.length === 0 ? (
              <p style={{ color: '#888', fontSize: 13, margin: 0 }}>No subjects yet. Click "Edit profile" to add one.</p>
            ) : (
              <div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 90px 110px', gap: 8, padding: '0 0 8px', borderBottom: '1px solid #f0f0f0', marginBottom: 4 }}>
                  <span style={{ fontSize: 11, color: '#aaa', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Subject</span>
                  <span style={{ fontSize: 11, color: '#aaa', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Level</span>
                  <span style={{ fontSize: 11, color: '#aaa', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Rate/month</span>
                </div>
                {subjects.map(s => {
                  const hasTemplate = ownTemplates.some(t => t.tutor_subject_id === s.subject_id);
                  const hasMaterial = ownMaterials.some(m => m.subject_id === s.subject_id);
                  return (
                    <div key={s.subject_id} style={{ display: 'grid', gridTemplateColumns: '1fr 90px 110px', gap: 8, alignItems: 'center', padding: '10px 0', borderBottom: '1px solid #f8f8f8' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                        <span style={{ fontWeight: 500, fontSize: 13 }}>{s.subject_name}</span>
                        {!hasTemplate && (
                          <Tag icon={<WarningOutlined />} color="orange" style={{ fontSize: 11, margin: 0 }}>
                            No Roadmap template linked
                          </Tag>
                        )}
                        {!hasMaterial && (
                          <Tag icon={<WarningOutlined />} color="gold" style={{ fontSize: 11, margin: 0 }}>
                            No materials uploaded
                          </Tag>
                        )}
                      </div>
                      <span style={{ fontSize: 12, color: '#555' }}>{s.level || '—'}</span>
                      <span style={{ fontSize: 12, color: '#555' }}>{s.monthly_rate ? `$${parseFloat(s.monthly_rate).toFixed(0)}/month` : '—'}</span>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* ── Availability (tutor own view) ── */}
        {isOwn && profile.role_name === 'tutor' && !editing && (
          <AvailabilityEditor userId={user.userId} />
        )}

        {/* ── Subjects & rates (non-owner view, grouped by level) ── */}
        {!isOwn && profile.role_name === 'tutor' && subjects.length > 0 && (
          <div style={{ border: '1px solid #e8e8e8', borderRadius: 12, background: '#fff', padding: '20px 24px', marginBottom: 20 }}>
            <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 12 }}>Subjects & rates</div>
            {groupedSubjects.map(group => (
              <div key={group.level} style={{ marginBottom: 16 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: '#aaa', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 8 }}>
                  {group.level}
                </div>
                {group.items.map((s, idx) => {
                  const subjectMaterials = preview?.materials.filter(m => m.subject_id === s.subject_id) || [];
                  const subjectTemplates = preview?.templates.filter(t => t.tutor_subject_id === s.subject_id) || [];
                  const hasPreview = subjectMaterials.length > 0 || subjectTemplates.length > 0;
                  return (
                    <div key={s.subject_id} style={{
                      display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                      padding: '10px 0',
                      borderBottom: idx < group.items.length - 1 ? '1px solid #f5f5f5' : 'none',
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <span style={{ fontWeight: 600, fontSize: 14 }}>{s.subject_name}</span>
                        {s.monthly_rate && <span style={{ fontSize: 13, color: '#555' }}>${parseFloat(s.monthly_rate).toFixed(0)}/month</span>}
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        {user?.role === 'student' && subscribedIds.has(s.subject_id) && (
                          <Tag color="success" style={{ fontSize: 11 }}>Subscribed</Tag>
                        )}
                        {hasPreview && (
                          <Button size="small" type="link" style={{ padding: 0, fontSize: 13 }}
                            onClick={() => { setPreviewSubjectId(s.subject_id); setPreviewStage(null); }}>
                            Preview →
                          </Button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
        )}

        {/* ── Preview modal ── */}
        {(() => {
          const s = subjects.find(s => s.subject_id === previewSubjectId);
          const subjectMaterials = preview?.materials.filter(m => m.subject_id === previewSubjectId) || [];
          const subjectTemplates = preview?.templates.filter(t => t.tutor_subject_id === previewSubjectId) || [];
          return (
            <Modal
              open={!!previewSubjectId}
              onCancel={() => { setPreviewSubjectId(null); setPreviewStage(null); }}
              maskClosable={false}
              footer={null}
              width="calc(100vw - 180px - 64px)"
              title={null}
              styles={{ body: { padding: 0 } }}
              style={{ margin: 0, top: 56, left: 180 + 32, paddingBottom: 0 }}
              maskStyle={{ left: 180 }}
            >
              {s && (
                <div>
                  <div style={{ padding: '32px 40px 24px', borderBottom: '1px solid #f0f0f0' }}>
                    <div style={{ fontSize: 11, fontWeight: 600, color: '#aaa', letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 6 }}>{profile.name}</div>
                    <div style={{ fontSize: 24, fontWeight: 700, color: '#0a0a0a', marginBottom: 4 }}>{s.subject_name}</div>
                    <div style={{ fontSize: 14, color: '#888' }}>
                      {s.level}{s.monthly_rate ? ` · $${parseFloat(s.monthly_rate).toFixed(0)}/month` : ''}
                    </div>
                  </div>
                  <div style={{ padding: '28px 40px 32px', display: 'flex', flexDirection: 'column', gap: 32 }}>
                    {subjectTemplates.length > 0 && (
                      <div>
                        <div style={{ fontSize: 11, fontWeight: 600, color: '#aaa', letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 16 }}>Learning roadmap</div>
                        {subjectTemplates.map(t => (
                          <div key={t.template_id} style={{ marginBottom: 20 }}>
                            <div style={{ fontSize: 15, fontWeight: 600, color: '#0a0a0a', marginBottom: 12 }}>
                              {t.title}
                              <span style={{ fontSize: 12, color: '#aaa', fontWeight: 400, marginLeft: 10 }}>{t.stages?.length || 0} stages</span>
                            </div>
                            <StagePipeline
                              stages={(t.stages || []).map((stage, i) => ({ ...stage, stage_status: i === 0 ? 'active' : 'locked', progress_id: stage.stage_id }))}
                              selected={previewStage}
                              onSelect={stage => setPreviewStage(prev => prev?.progress_id === stage.progress_id ? null : stage)}
                              clickable={false} previewMode
                            />
                            {previewStage && (
                              <div style={{ marginTop: 16, padding: '16px 20px', background: '#f9f9f9', borderRadius: 8, borderLeft: '3px solid #0a0a0a' }}>
                                <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 6 }}>{previewStage.stage_name}</div>
                                <div style={{ fontSize: 13, color: '#555' }}>
                                  {previewStage.gate_description
                                    ? <><span style={{ fontWeight: 600 }}>Gate description: </span>{previewStage.gate_description}</>
                                    : <span style={{ color: '#aaa' }}>No gate description set.</span>}
                                </div>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                    {subjectMaterials.length > 0 && (
                      <div>
                        <div style={{ fontSize: 11, fontWeight: 600, color: '#aaa', letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 16 }}>Sample materials</div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                          {subjectMaterials.map(m => (
                            <div key={m.material_id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 16px', border: '1px solid #f0f0f0', borderRadius: 8 }}>
                              <FilePdfOutlined style={{ fontSize: 20, color: '#0a0a0a', flexShrink: 0 }} />
                              <div style={{ flex: 1, minWidth: 0 }}>
                                <a href={m.material_link} target="_blank" rel="noreferrer"
                                  style={{ fontWeight: 500, fontSize: 14, color: '#0a0a0a' }}>{m.file_name || 'Unnamed file'}</a>
                                <div style={{ fontSize: 12, color: '#aaa', marginTop: 2 }}>{dayjs(m.uploaded_at).format('D MMM YYYY')}</div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    {user?.role === 'student' && !subscribedIds.has(s.subject_id) && (
                      <div style={{ borderTop: '1px solid #f0f0f0', paddingTop: 24, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <div>
                          <div style={{ fontWeight: 600, fontSize: 15 }}>Ready to get started?</div>
                          <div style={{ fontSize: 13, color: '#888', marginTop: 2 }}>
                            Subscribe to unlock all {subjectTemplates[0]?.stages?.length || ''} stages and full materials.
                          </div>
                        </div>
                        <Button type="primary" size="large" style={{ background: '#0a0a0a', border: 'none', paddingLeft: 28, paddingRight: 28 }}
                          onClick={() => {
                            setPreviewSubjectId(null);
                            setSelectedSubject(s);
                            subForm.setFieldsValue({ tutor_subject_id: s.subject_id, months: '3', monthly_rate: s.monthly_rate ? parseFloat(s.monthly_rate).toFixed(0) : undefined });
                            setSubModal(true);
                          }}>Subscribe</Button>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </Modal>
          );
        })()}

        {/* ── Subscribe modal ── */}
        <Modal title={`Subscribe to ${profile.name}`} open={subModal} maskClosable={false}
          onCancel={() => { setSubModal(false); subForm.resetFields(); setSelectedSubject(null); }} footer={null}>
          <Form form={subForm} layout="vertical" onFinish={subscribe} requiredMark={false} style={{ marginTop: 8 }}
            onValuesChange={(changed) => {
              if ('tutor_subject_id' in changed) {
                const s = subjects.find(s => s.subject_id === changed.tutor_subject_id);
                setSelectedSubject(s || null);
              }
            }}>
            <Form.Item label="Subject" name="tutor_subject_id" rules={[{ required: true, message: 'Please select a subject' }]}>
              <Select size="large" placeholder="Select a subject">
                {subjects
                  .filter(s => !subscribedIds.has(s.subject_id))
                  .filter(s => {
                    const sl = levelFromCurrentLevel(user?.current_level);
                    return !sl || s.level === sl;
                  })
                  .map(s => (
                    <Select.Option key={s.subject_id} value={s.subject_id}>
                      {s.subject_name} · {s.level}
                    </Select.Option>
                  ))
                }
              </Select>
            </Form.Item>
            {selectedSubject && (
              <div style={{
                background: selectedSubject.monthly_rate ? '#f0f9f0' : '#f5f5f5',
                border: `1px solid ${selectedSubject.monthly_rate ? '#b7eb8f' : '#e8e8e8'}`,
                borderRadius: 8, padding: '12px 16px', marginBottom: 16,
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              }}>
                <div>
                  <div style={{ fontSize: 12, color: '#888', marginBottom: 2 }}>Monthly rate</div>
                  <div style={{ fontSize: 20, fontWeight: 700, color: '#0a0a0a' }}>
                    {selectedSubject.monthly_rate
                      ? `$${parseFloat(selectedSubject.monthly_rate).toFixed(0)}/month`
                      : <span style={{ fontSize: 14, color: '#aaa', fontWeight: 400 }}>Not set by tutor</span>
                    }
                  </div>
                </div>
                <div style={{ textAlign: 'right', fontSize: 12, color: '#888' }}>
                  <div>{selectedSubject.subject_name}</div>
                  <div>{selectedSubject.level}</div>
                </div>
              </div>
            )}
            <Form.Item label="Duration" name="months" rules={[{ required: true }]} initialValue="3">
              <Select size="large">
                <Select.Option value="1">1 month</Select.Option>
                <Select.Option value="3">3 months</Select.Option>
                <Select.Option value="6">6 months</Select.Option>
                <Select.Option value="12">12 months</Select.Option>
              </Select>
            </Form.Item>
            <Button type="primary" htmlType="submit" block size="large" loading={subLoading}
              style={{ background: '#0a0a0a', border: 'none', marginTop: 8 }}>
              Confirm subscription
            </Button>
          </Form>
        </Modal>
      </div>
    </AppLayout>
  );
}
